/**
 * Which audience's surface a route belongs to (PIX-604).
 *
 * One build serves two audiences: students, and the teachers and administrators
 * who run a school's panel. They share a repository, a bundle, a domain and a
 * PostHog project, so `surface` is the dimension that keeps their funnels
 * apart. Without it school traffic silently inflates every student metric and
 * neither audience can be looked at on its own.
 *
 * Pure and framework-free so it unit tests in plain node, and deliberately the
 * only place the organisation route prefixes are written down. Session replay
 * (PIX-602) consumes this too, where a misclassification does not merely skew
 * a metric - it records someone it should not.
 */
import { SURFACES } from './events.js';

/**
 * Route path prefixes belonging to the organisation surface.
 *
 * Prefixes rather than a per-route `meta` flag because every new panel route is
 * added beneath `/org` and is therefore classified without anyone remembering
 * to annotate it. The cost is that a new TOP-LEVEL organisation route has to be
 * added here - and that has already been missed once in spirit:
 * `/worksheet-upload` is the unauthenticated phone page for the
 * worksheet-builder QR handoff, and it sits outside both of the prefixes you
 * would think to write down. The regression guard in
 * tests/instrumentationHygiene.test.js fails the build if another appears.
 */
export const ORGANISATION_PATH_PREFIXES = Object.freeze([
  '/org',
  '/organisation',
  '/teachers',
  '/register-organisation',
  '/worksheet-upload',
]);

/**
 * True when `path` is exactly `prefix` or nested beneath it.
 *
 * Segment-aware on purpose. A bare `startsWith` would make `/org` swallow
 * `/organisation` - which happens to be harmless, since both are organisation
 * surfaces - but it would also make any future `/teachers-guide` marketing page
 * panel traffic, which is not harmless at all.
 */
function isUnder(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Resolve a route path to its surface.
 *
 * Anything not explicitly organisation is student. The default runs that way
 * round because the student app is much the larger surface and gains routes
 * constantly - a new student route must never need registering anywhere to be
 * counted correctly. The trade is that a forgotten organisation route counts as
 * student traffic, which is precisely what the hygiene guard exists to catch.
 */
export function resolveSurface(path) {
  if (typeof path !== 'string' || !path) {
    return SURFACES.STUDENT;
  }

  return ORGANISATION_PATH_PREFIXES.some((prefix) => isUnder(path, prefix))
    ? SURFACES.ORGANISATION
    : SURFACES.STUDENT;
}
